import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useEditor, AnyExtension, Editor } from "@tiptap/react";
import { tipTapExtensions } from "../features/tipTap/TipTapExtensions";

type EditorContextType = {
    editor: React.RefObject<Editor | null>;
};

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export default function EditorContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const editorRef = useRef<Editor | null>(null);
    window.editorRef = editorRef;

    return (
        <EditorContext.Provider value={{ editor: editorRef }}>
            {children}
            <EditorNoUpdate
                setEditor={(editor) => (editorRef.current = editor)}
            />
        </EditorContext.Provider>
    );
}

// I'm lowering the state of the editor to avoid unnecessary re-renders when editor content changes
function EditorNoUpdate(props: { setEditor: (editor: Editor | null) => void }) {
    const editor = useEditor({
        extensions: tipTapExtensions as AnyExtension[],
        content: "",
        autofocus: false,
        editable: true,
        editorProps: {
            attributes: {
                'aria-label': 'Text editor',
                'aria-describedby': 'editor-description',
                'role': 'textbox',
                'aria-multiline': 'true',
                'alt': 'Text editor input area'
            }
        }
    });

    useEffect(() => {
        props.setEditor(editor);
    }, [editor]);

    return <></>;
}

export const useGetEditor = () => {
    const context = useContext(EditorContext);
    if (!context) {
        throw new Error(
            "useEditorContext must be used within a EditorProvider"
        );
    }
    return context;
};
